import { useCallback, useEffect, useState } from 'react'
import { ReactState } from '../../React/Common/Contexts'
import { ALL_VALUE } from '../../React/Common/Constants/DefaultValues'
import { isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'

export enum SORT_DIRECTION {
    ASCENDING = 'asc',
    DESCENDING = 'desc'
}
export type SortDirection = SORT_DIRECTION.ASCENDING | SORT_DIRECTION.DESCENDING | undefined

export type SorterFn<T> = (a: T, b: T, sortDirection?: SortDirection) => number
export type SortTableFn<T, K extends keyof T> = (
    sorter?: SorterFn<T>,
    sortField?: K,
    sortDirection?: SortDirection
) => void
export type NextSortDirectionFn = (sortDirection: SortDirection) => SortDirection

export const defaultSortingSequence: SortDirection[] = [SORT_DIRECTION.ASCENDING, SORT_DIRECTION.DESCENDING, undefined]

const getNextSortDirection: NextSortDirectionFn = (direction: SortDirection): SortDirection => {
    const idx: number = defaultSortingSequence.indexOf(direction)
    if (idx === ALL_VALUE || idx + 1 >= defaultSortingSequence.length) {
        return defaultSortingSequence[0]
    }
    return defaultSortingSequence[idx + 1]
}

export type useSortReturn<T, K extends keyof T> = [
    T[],
    SortTableFn<T, K>,
    SortDirection,
    Definable<K>,
    () => SortDirection
]

type useSortHook = <T, K extends keyof T>(data: T[], sortServer: boolean) => useSortReturn<T, K>

export const useSort: useSortHook = <T, K extends keyof T>(data: T[], sortServer: boolean): useSortReturn<T, K> => {
    const [tableData, setTableData]: ReactState<T[]> = useState<T[]>(data)
    const [direction, setDirection]: ReactState<SortDirection> = useState<SortDirection>(undefined)
    const [orderBy, setOrderBy]: ReactState<Definable<K>> = useState<Definable<K>>()

    useEffect((): void => {
        setTableData(data)
    }, [data])

    const sortData: SortTableFn<T, K> = useCallback(
        (sorter?: SorterFn<T>, sortField?: K, sortDirection?: SortDirection): void => {
            setDirection(sortDirection || undefined)
            setOrderBy(sortField)
            if (sortServer) {
                return
            }
            if (sorter) {
                setTableData([...tableData].sort((a: T, b: T): number => sorter(a, b, sortDirection)))
                return
            }
            /**
             * Default sort function using localeCompare.
             * If default local sorting is sorting incorrectly here is where we need to look.
             */
            if (sortField && sortDirection !== undefined) {
                const sorted: T[] = [...tableData].sort((a: T, b: T): number => {
                    const valueA: T[K] = a[sortField]
                    const valueB: T[K] = b[sortField]
                    if (!isVarNotEmpty(valueA)) {
                        return 1
                    }
                    if (!isVarNotEmpty(valueB)) {
                        return ALL_VALUE
                    }
                    const sortWeight: number = sortDirection === SORT_DIRECTION.ASCENDING ? 1 : ALL_VALUE
                    // Compiler complains on line below, we check null and undefined but compiler doesn't like because it's done in IsVarEmpty
                    return valueA.toString().localeCompare(valueB.toString(), 'en', { numeric: true }) * sortWeight
                })
                setTableData(sorted)
            }
            if (sortDirection === undefined) {
                setTableData([...data])
            }
        },
        [data, tableData, sortServer]
    )

    const nextSortDirection: () => SortDirection = useCallback(
        (): SortDirection => getNextSortDirection(direction),
        [direction]
    )

    return [tableData, sortData, direction, orderBy, nextSortDirection]
}
